import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { useSettings } from '../../context/SettingsContext';
import { mobileApi } from '../../services/api';
import { enqueueWoComplete } from '../../services/woQueue';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import SignaturePad from 'signature_pad';
import {
  Play, Pause, ChevronRight, Loader2, Camera, X, Plus, Check, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const formatTime = (s) => {
  if (!s) return '00:00:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), c = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(c).padStart(2, '0')}`;
};
const overdueDays = (wo) => {
  if (!wo.scheduled_date || wo.status === 'completed') return null;
  const d = Math.floor((Date.now() - new Date(wo.scheduled_date).getTime()) / 86400000);
  return d > 0 ? d : null;
};
const assetLine = (wo) =>
  [[wo.asset_make, wo.asset_model].filter(Boolean).join(' '), wo.asset_year, wo.asset_location]
    .filter(Boolean).join(' · ');

const DefRow = ({ label, children }) => (
  <div className="flex items-center gap-3 py-2.5" style={{ minHeight: 42 }}>
    <span className="text-[12.5px] text-[var(--ink-tertiary)] shrink-0" style={{ width: 96 }}>{label}</span>
    <span className="text-[13.5px] font-semibold text-[var(--ink-primary)] text-right flex-1 truncate">{children}</span>
  </div>
);

const MobileWorkOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useMobileAuth();
  const { formatDate } = useSettings();

  const [loading, setLoading] = useState(true);
  const [wo, setWo] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [workNotes, setWorkNotes] = useState('');

  const [signoff, setSignoff] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [sigDirty, setSigDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addingTask, setAddingTask] = useState(false);
  const [newTask, setNewTask] = useState('');

  const canvasRef = useRef(null);
  const padRef = useRef(null);

  const load = async () => {
    try {
      const res = await mobileApi.getWorkOrder(id, getToken());
      setWo(res.data);
      setElapsed(res.data.total_time_seconds || 0);
      setWorkNotes(res.data.notes || '');
    } catch { toast.error('Failed to load work order'); navigate('/mobile/work-orders'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (wo?.timer_is_running && wo?.timer_started_at) {
      const start = new Date(wo.timer_started_at);
      const iv = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000) + (wo.total_time_seconds || 0)), 1000);
      return () => clearInterval(iv);
    }
  }, [wo?.timer_is_running, wo?.timer_started_at, wo?.total_time_seconds]);

  // Signature pad — white field, dark stroke (§3.4)
  useEffect(() => {
    if (signoff && canvasRef.current && !padRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth * 2;
      canvas.height = canvas.offsetHeight * 2;
      canvas.getContext('2d').scale(2, 2);
      padRef.current = new SignaturePad(canvas, { backgroundColor: 'rgb(255,255,255)', penColor: 'rgb(26,26,26)' });
      padRef.current.addEventListener('endStroke', () => setSigDirty(!padRef.current.isEmpty()));
    }
    if (!signoff && padRef.current) { padRef.current = null; }
  }, [signoff]);

  const tasks = (wo?.tasks || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
  const hasTasks = tasks.length > 0;
  const doneCount = tasks.filter((t) => t.completed).length;
  const outstanding = tasks.filter((t) => !t.completed);
  const allDone = !hasTasks || outstanding.length === 0;

  const toggleTask = async (taskId, cur) => {
    try { await mobileApi.toggleTask(wo.id, taskId, { completed: !cur }, getToken()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update task'); }
  };
  const addTask = async () => {
    const d = newTask.trim();
    if (!d) return;
    try {
      await mobileApi.addTask(wo.id, { description: d }, getToken());
      setNewTask('');
      setAddingTask(false);
      load();
    } catch { toast.error('Failed to add task'); }
  };
  const startTimer = async () => { try { await mobileApi.startWorkOrderTimer(id, getToken()); load(); } catch { toast.error('Failed'); } };
  const pauseTimer = async () => { try { await mobileApi.pauseWorkOrderTimer(id, getToken()); load(); } catch { toast.error('Failed'); } };
  const saveNotes = async () => {
    // notes persist with the work order via completion payload; keep locally for now
    setShowNotes(false);
    toast.success('Notes saved to this job');
  };
  const handlePhotos = (e) => {
    Array.from(e.target.files).forEach((f) => {
      const r = new FileReader(); r.onload = (ev) => setPhotos((p) => [...p, ev.target.result]); r.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const doSignOff = async () => {
    if (!completionNotes.trim() || padRef.current?.isEmpty()) return;
    const payload = { notes: completionNotes.trim(), work_notes: workNotes, photos, signature: padRef.current.toDataURL('image/png') };
    if (!navigator.onLine) {
      enqueueWoComplete(id, payload);
      toast.success('Signed off offline — it will send when you have signal');
      navigate('/mobile/work-orders');
      return;
    }
    setSubmitting(true);
    try {
      await mobileApi.completeWorkOrder(id, payload, getToken());
      toast.success('Work order signed off');
      navigate('/mobile/work-orders');
    } catch (e) {
      if (!e.response) { enqueueWoComplete(id, payload); toast.success('Signed off offline — it will send when you have signal'); navigate('/mobile/work-orders'); }
      else toast.error(e.response?.data?.detail || 'Failed to sign off');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[var(--surface-page)] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[var(--volt-icon)] animate-spin" /></div>;
  if (!wo) return null;

  const woRef = wo.reference || wo.work_order_number || '';
  const assetId = wo.asset_unit_number || wo.asset_name || '—';
  const od = overdueDays(wo);
  const offline = typeof navigator !== 'undefined' && !navigator.onLine;
  const loggedMin = Math.round(elapsed / 60);

  // ── SIGN OFF (4c) ────────────────────────────────────────────────────────
  if (signoff) {
    const ready = completionNotes.trim().length > 0 && sigDirty;
    return (
      <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="wo-signoff">
        <MobileHeader title="Sign off work order" showBack onBack={() => setSignoff(false)} />
        <div className="px-4 py-4 space-y-5">
          <MobileCard padding="normal">
            <div className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">
              {assetId} <span className="font-sans font-medium text-[var(--ink-secondary)]">{wo.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-1 text-[13px]">
              <span className="shrink-0 rounded-full" style={{ width: 7, height: 7, background: 'var(--pass)' }} />
              <span className="text-[var(--pass-ink)] font-medium">{hasTasks ? `${doneCount} of ${tasks.length} tasks done` : 'No task list'}</span>
              <span className="text-[var(--ink-tertiary)]">· Logged <span className="font-mono">{formatTime(elapsed)}</span></span>
            </div>
          </MobileCard>

          <div>
            <label className="block text-[13px] font-semibold text-[var(--ink-secondary)] mb-2">What did you do?</label>
            <textarea
              value={completionNotes} onChange={(e) => setCompletionNotes(e.target.value)}
              placeholder="Describe the work performed" data-testid="wo-completion-notes"
              className="w-full min-h-[78px] rounded-[12px] bg-white border border-[var(--border-strong)] px-4 py-3 text-[16px] leading-snug text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {photos.map((p, i) => (
              <div key={i} className="relative" style={{ width: 56, height: 56 }}>
                <img src={p} alt="" className="w-full h-full object-cover rounded-[12px]" />
                <button onClick={() => setPhotos((prev) => prev.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white flex items-center justify-center" style={{ border: '1px solid var(--border-strong)' }}><X className="w-3 h-3" /></button>
              </div>
            ))}
            <label className="flex items-center justify-center rounded-[12px] cursor-pointer" style={{ width: 56, height: 56, border: '1.5px dashed var(--border-strong)' }} data-testid="wo-signoff-photo">
              <Camera className="w-5 h-5 text-[var(--ink-tertiary)]" />
              <input type="file" accept="image/*" capture="environment" multiple onChange={handlePhotos} className="hidden" />
            </label>
            <span className="text-[13px] text-[var(--ink-secondary)]">{photos.length} photo{photos.length === 1 ? '' : 's'} · optional</span>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[13px] font-semibold text-[var(--ink-secondary)]">Signature</label>
              <button onClick={() => { padRef.current?.clear(); setSigDirty(false); }} className="text-[13px] font-semibold text-[var(--volt-ink)]">Clear</button>
            </div>
            <div className="relative rounded-[12px] overflow-hidden bg-white" style={{ height: 112, border: '1.5px solid #C4BFB3' }}>
              <canvas ref={canvasRef} className="w-full h-full touch-none" style={{ touchAction: 'none' }} data-testid="wo-signature-pad" />
              <span className="absolute bottom-1.5 left-3 font-mono text-[10.5px] text-[var(--ink-tertiary)] pointer-events-none">A. Roche · {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
          <p className="text-[12.5px] text-[var(--ink-secondary)] leading-snug mb-2.5" data-testid="wo-signoff-consequence">
            Closes <b className="text-[var(--ink-primary)]">{woRef}</b>, logs {loggedMin} min against <b className="text-[var(--ink-primary)]">{assetId}</b> and returns it to service.
            {offline && <> <b className="text-[var(--ink-primary)]">You are offline</b> — saved here and sent when signal returns.</>}
          </p>
          {ready ? (
            <button onClick={doSignOff} disabled={submitting} data-testid="wo-signoff-btn"
              className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center active:scale-[0.99] transition-transform"
              style={{ height: 58, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'SIGN OFF'}
            </button>
          ) : (
            <>
              <div className="w-full rounded-[13px] flex items-center justify-center" style={{ height: 58, background: '#E6E2D9', border: '1px solid #D3CEC2', color: '#A29D93', fontWeight: 800 }}>SIGN OFF</div>
              <p className="text-center text-[12.5px] mt-2" style={{ color: '#9A3200' }}>{!completionNotes.trim() ? 'Say what you did and sign' : 'Add your signature'}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── DETAIL (4b) ────────────────────────────────────────────────────────
  const isCompleted = wo.status === 'completed';
  const running = wo.timer_is_running;
  return (
    <div className="min-h-screen bg-[var(--surface-page)] pb-40" data-testid="wo-detail">
      <MobileHeader
        showBack
        title={<span className="flex flex-col leading-tight">
          <span className="font-mono text-[10.5px] font-normal text-[var(--ink-tertiary)]">{woRef}</span>
          <span className="text-[15.5px] font-bold text-[var(--ink-primary)] truncate">{wo.title}</span>
        </span>}
      />

      <div className="px-4 py-4 space-y-4">
        {/* asset card */}
        <MobileCard spine={od ? 'var(--overdue)' : 'var(--border)'} onClick={() => navigate(`/mobile/assets/${wo.asset_id}`)} padding="normal">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[14px] font-bold text-[var(--ink-primary)]">{assetId}</span>
                {od && <span className="inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[11px] font-semibold border bg-[var(--overdue-bg)] text-[var(--overdue-ink)] border-[var(--overdue-edge)]">OVERDUE {od}D</span>}
              </div>
              {assetLine(wo) && <div className="text-[12.5px] text-[var(--ink-secondary)] mt-0.5 truncate">{assetLine(wo)}</div>}
            </div>
            <ChevronRight className="w-5 h-5 text-[var(--ink-tertiary)] shrink-0" />
          </div>
          {wo.asset_has_device && (
            <button onClick={(e) => { e.stopPropagation(); navigate(`/mobile/assets/${wo.asset_id}`); }} className="w-full flex items-center gap-2 mt-3 pt-3 border-t border-[var(--divider)] text-[13px] font-semibold text-[var(--volt-ink)]">
              <MapPin className="w-4 h-4" /> Locate asset
            </button>
          )}
        </MobileCard>

        {/* timer */}
        {!isCompleted && (
          <div className="bg-white rounded-[13px] px-4 flex items-center justify-between" style={{ minHeight: 62, border: '1px solid var(--border)', boxShadow: 'var(--elev-raised)' }}>
            <div>
              <div className="font-mono text-[21px] font-bold text-[var(--ink-primary)] leading-none">{formatTime(elapsed)}</div>
              <div className="text-[12px] text-[var(--ink-tertiary)] mt-1">Time logged{running ? ' · running' : ''}</div>
            </div>
            <button onClick={running ? pauseTimer : startTimer} data-testid="wo-timer-btn"
              className="flex items-center gap-1.5 rounded-[12px] bg-white text-[var(--ink-primary)] text-[13px] font-semibold px-4"
              style={{ height: 44, border: '1.5px solid var(--border-strong)' }}>
              {running ? <><Pause className="w-4 h-4" /> Pause</> : <><Play className="w-4 h-4 fill-current" /> Start</>}
            </button>
          </div>
        )}

        {/* details */}
        <MobileCard padding="normal">
          <div className="divide-y divide-[var(--divider)]">
            <DefRow label="Scheduled">{wo.scheduled_date ? formatDate(wo.scheduled_date) : 'Not scheduled'}</DefRow>
            {wo.assigned_name && <DefRow label="Assigned to">{wo.assigned_name}</DefRow>}
            {wo.defect_id && <DefRow label="Raised from">{[wo.defect_number, wo.defect_title].filter(Boolean).join(' — ')}</DefRow>}
          </div>
        </MobileCard>

        {/* tasks */}
        {!(isCompleted && !hasTasks) && (
          <MobileCard padding="none">
            <div className={`flex items-center justify-between px-4 bg-[var(--surface-sunken)] ${hasTasks || addingTask ? 'rounded-t-[13px]' : 'rounded-[13px]'}`} style={{ height: 42 }}>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-[0.11em] text-[var(--ink-tertiary)]">Tasks</span>
                {hasTasks
                  ? <span className={`font-mono text-[11.5px] font-bold ${allDone ? 'text-[var(--pass-ink)]' : 'text-[var(--overdue-ink)]'}`}>{doneCount} of {tasks.length} done</span>
                  : <span className="text-[11.5px] text-[var(--ink-tertiary)]">No task list on this job</span>}
              </div>
              {!isCompleted && !addingTask && <button onClick={() => setAddingTask(true)} data-testid="wo-add-task-btn" className="text-[13px] font-semibold text-[var(--volt-ink)]">Add</button>}
            </div>
            <div className="px-4">
              {tasks.map((t, i) => (
                <div key={t.id} className={`flex items-center gap-3 ${i ? 'border-t border-[var(--divider)]' : ''}`} style={{ minHeight: 52 }} data-testid={`wo-task-${t.id}`}>
                  <button
                    onClick={() => !isCompleted && toggleTask(t.id, t.completed)}
                    className="shrink-0 flex items-center justify-center"
                    style={t.completed
                      ? { width: 24, height: 24, borderRadius: 6, background: '#0E6B3F' }
                      : { width: 24, height: 24, borderRadius: 6, background: '#fff', border: '1.5px solid #1A1A1A' }}
                  >
                    {t.completed && <Check className="w-4 h-4 text-white" />}
                  </button>
                  <span className={`text-[14px] ${t.completed ? 'text-[var(--ink-tertiary)] line-through' : 'text-[var(--ink-primary)] font-semibold'}`}>{t.description}</span>
                </div>
              ))}
              {addingTask && (
                <div className={`flex items-center gap-2 py-2.5 ${tasks.length ? 'border-t border-[var(--divider)]' : ''}`}>
                  <input
                    autoFocus
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addTask(); if (e.key === 'Escape') { setAddingTask(false); setNewTask(''); } }}
                    placeholder="Describe the task"
                    data-testid="wo-new-task-input"
                    className="flex-1 h-11 rounded-[10px] bg-white border border-[var(--border-strong)] px-3 text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A] focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]"
                  />
                  <button onClick={addTask} data-testid="wo-save-task-btn" disabled={!newTask.trim()}
                    className="h-11 px-4 rounded-[10px] text-[13px] font-bold bg-[var(--volt)] text-black disabled:opacity-40" style={{ border: '1px solid var(--volt-edge)' }}>Add</button>
                  <button onClick={() => { setAddingTask(false); setNewTask(''); }} className="h-11 w-11 rounded-[10px] flex items-center justify-center" style={{ border: '1px solid var(--border-strong)' }}>
                    <X className="w-4 h-4 text-[var(--ink-tertiary)]" />
                  </button>
                </div>
              )}
            </div>
          </MobileCard>
        )}

        {/* work notes */}
        {!isCompleted && (
          showNotes ? (
            <MobileCard padding="normal">
              <textarea value={workNotes} onChange={(e) => setWorkNotes(e.target.value)} placeholder="Notes about work performed"
                className="w-full min-h-[84px] rounded-[12px] bg-white border border-[var(--border-strong)] px-3 py-2 text-[16px] text-[var(--ink-primary)] outline-none focus:border-[#1A1A1A]" />
              <button onClick={saveNotes} className="mt-2 text-[13px] font-semibold text-[var(--volt-ink)]">Save note</button>
            </MobileCard>
          ) : (
            <button onClick={() => setShowNotes(true)} className="w-full bg-white rounded-[13px] px-4 flex items-center justify-between" style={{ height: 48, border: '1px solid var(--border)', boxShadow: 'var(--elev-raised)' }} data-testid="wo-add-notes">
              <span className="text-[13px] font-semibold text-[var(--volt-ink)]">{workNotes ? 'Edit work notes' : 'Add work notes'}</span>
              <span className="text-[12px] text-[var(--ink-tertiary)]">Optional</span>
            </button>
          )
        )}
      </div>

      {/* bottom bar */}
      {!isCompleted && (
        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pt-2.5 pb-4 bg-[var(--surface-page)]" style={{ boxShadow: 'var(--elev-floating)' }}>
          {allDone ? (
            <button onClick={() => setSignoff(true)} data-testid="wo-complete-btn"
              className="w-full rounded-[13px] bg-[var(--volt)] text-black flex items-center justify-center active:scale-[0.99] transition-transform"
              style={{ height: 56, fontWeight: 800, border: '1px solid var(--volt-edge)' }}>
              COMPLETE WORK ORDER
            </button>
          ) : (
            <>
              <div className="w-full rounded-[13px] flex items-center justify-center" data-testid="wo-complete-blocked"
                style={{ height: 56, background: '#E6E2D9', border: '1px solid #D3CEC2', color: '#A29D93', fontWeight: 800 }}>
                COMPLETE WORK ORDER
              </div>
              <p className="flex items-center justify-center gap-1.5 text-center text-[12.5px] mt-2" style={{ color: '#9A3200' }}>
                <span className="rounded-full" style={{ width: 6, height: 6, background: '#9A3200' }} />
                {outstanding.length} task{outstanding.length === 1 ? '' : 's'} left: {outstanding[0]?.description}
              </p>
            </>
          )}
        </div>
      )}

    </div>
  );
};

export default MobileWorkOrderDetail;
